<template>
  <!-- "CI von Webseite" (spec 2026-10-03 §9): input, two real steps, review per field, apply.
       Applying goes back to the parent, which writes with today's methods (C10). -->
  <div class="cw" data-test="ci-import">
    <div class="cw-kopf">
      <div>
        <h4 class="cw-titel">{{ t('titel') }}</h4>
        <p class="cw-unter">{{ t(schritt === 'pruefen' ? 'unterPruefen' : 'unter') }}</p>
      </div>
      <button type="button" class="cw-link" data-test="ci-import-zu" @click="schliessen">{{ $t('settings.ciBrandKit.zurueck') }}</button>
    </div>

    <!-- Step 1: input -->
    <form v-if="schritt === 'eingabe'" class="cw-karte cw-eingabe" data-test="ci-import-eingabe" @submit.prevent="lesen">
      <label class="cw-feld-label" for="cw-url-0">{{ t('adresse') }}</label>
      <div v-for="(u, i) in urls" :key="i" class="cw-url-zeile">
        <input
          :id="`cw-url-${i}`"
          v-model="urls[i]"
          type="url"
          inputmode="url"
          class="cw-input"
          :placeholder="i === 0 ? 'https://www.beispiel.de' : 'https://www.beispiel.de/ueber-uns'"
          :data-test="`ci-import-url-${i}`"
        >
        <button v-if="i > 0" type="button" class="cw-link cw-weg" :aria-label="t('seiteWeg')" @click="urls.splice(i, 1)">×</button>
      </div>
      <button v-if="urls.length < maxSeiten" type="button" class="cw-link cw-mehr" data-test="ci-import-mehr" @click="urls.push('')">{{ t('weitereSeite') }}</button>
      <p v-if="adressFehler === 'domain'" class="cw-fehler-text">{{ t('fehler.domain') }}</p>
      <p class="cw-hinweis">{{ t('hinweis') }}</p>
      <label class="cw-haken" data-test="ci-import-erlaubt">
        <input v-model="erlaubt" type="checkbox">
        <span>{{ t('erlaubt') }}</span>
      </label>
      <div class="cw-aktionen">
        <button type="submit" class="cw-knopf" :disabled="!!adressFehler || !erlaubt" data-test="ci-import-lesen">{{ t('lesen') }}</button>
      </div>
    </form>

    <!-- Step 2: two real steps -->
    <div v-else-if="schritt === 'warten'" class="cw-karte" data-test="ci-import-warten">
      <ol class="cw-schritte">
        <li v-for="s in ['lesen', 'zuordnen']" :key="s" class="cw-schritt" :class="`cw-${phase[s]}`" :data-test="`ci-import-schritt-${s}`">
          <span class="cw-schritt-zeichen" aria-hidden="true" />
          <span class="cw-schritt-text">
            <span class="cw-schritt-titel">{{ t(`schritt.${s}.${phase[s]}`) }}</span>
            <span v-if="s === 'lesen' && kurz" class="cw-schritt-info">{{ kurzText }}</span>
            <span v-for="w in s === 'lesen' && kurz ? kurzHinweise : []" :key="w" class="cw-schritt-info">{{ w }}</span>
          </span>
        </li>
      </ol>
      <p v-if="fehler" class="cw-fehler-text" data-test="ci-import-fehler">{{ t(`fehler.${fehler}`) }}</p>
      <div class="cw-aktionen">
        <button v-if="fehler && token" type="button" class="cw-knopf" data-test="ci-import-nochmal" @click="zuordnen">{{ t('nochmal') }}</button>
        <button v-if="fehler" type="button" class="cw-knopf-leise" @click="zurEingabe">{{ t('zurueckEingabe') }}</button>
        <button v-else type="button" class="cw-knopf-leise" data-test="ci-import-abbrechen" @click="zurEingabe">{{ t('abbrechen') }}</button>
      </div>
    </div>

    <!-- Step 3: review -->
    <div v-else class="cw-pruefen" data-test="ci-import-pruefen">
      <div class="cw-liste">
        <ki-kosten-zeile :kosten="vorschlag.kosten" class="cw-kosten" />
        <section v-for="g in gruppen" :key="g.key" class="cw-gruppe" :data-test="`ci-import-gruppe-${g.key}`">
          <div class="cw-gruppe-kopf">
            <h5 class="cw-gruppe-titel">{{ gruppenName(g.key) }} <span class="cw-zahl">{{ g.zeilen.length }}</span></h5>
            <button type="button" class="cw-link" @click="gruppeSetzen(g, !g.alleAn)">{{ t(g.alleAn ? 'alleAus' : 'alleAn') }}</button>
          </div>
          <label
            v-for="z in g.zeilen"
            :key="z.id"
            class="cw-zeile"
            :class="{ 'cw-rot': !!auswahl.rot[z.id] || (z.status === 'lesbarkeit' && !an[z.id]) }"
            :data-test="`ci-import-zeile-${z.id}`"
          >
            <input v-model="an[z.id]" type="checkbox" class="cw-check">
            <span class="cw-zeile-text">
              <span class="cw-name">{{ feldName(z.pfad) }}</span>
              <span class="cw-grund">
                <span class="cw-punkt" :class="`cw-${sicherheitStufe(z.sicherheit)}`" :title="t(`sicherheit.${sicherheitStufe(z.sicherheit)}`)" />
                {{ z.grund }}
              </span>
              <span v-if="markText(z)" class="cw-mark" :class="{ 'cw-mark-rot': markRot(z) }">{{ markText(z) }}</span>
            </span>
            <span class="cw-werte">
              <cw-wert :d="anzeige(z.pfad, wertJetzt(z))" />
              <span class="cw-pfeil" aria-hidden="true">→</span>
              <cw-wert :d="anzeige(z.pfad, z.wert)" />
            </span>
          </label>
        </section>

        <section v-if="hinweise.length || waechter.length" class="cw-gruppe cw-notizen" data-test="ci-import-hinweise">
          <h5 class="cw-gruppe-titel">{{ t('hinweise') }}</h5>
          <p v-for="h in hinweise" :key="h" class="cw-notiz">{{ h }}</p>
          <p v-for="w in waechter" :key="w" class="cw-notiz cw-notiz-waechter">{{ w }}</p>
        </section>

        <section v-if="verworfene.length" class="cw-gruppe cw-verworfen">
          <button type="button" class="cw-aufklapp" :aria-expanded="String(zeigeVerworfene)" data-test="ci-import-verworfen" @click="zeigeVerworfene = !zeigeVerworfene">
            {{ t('nichtUebernommen', { n: verworfene.length }) }}<span class="cw-chevron" :class="{ offen: zeigeVerworfene }" aria-hidden="true" />
          </button>
          <div v-if="zeigeVerworfene">
            <p v-for="z in verworfene" :key="z.id" class="cw-notiz">
              <strong>{{ feldName(z.pfad) }}</strong>: {{ t(`meldung.${z.meldung || 'ungueltig'}`) }}
            </p>
          </div>
        </section>

        <div class="cw-fuss" data-test="ci-import-fuss">
          <p v-if="sperreText" class="cw-fehler-text">{{ sperreText }}</p>
          <label v-if="lesbarkeitRot && !schriftRot" class="cw-haken" data-test="ci-import-bewusst">
            <input v-model="bewusst" type="checkbox">
            <span>{{ t('bewusst') }}</span>
          </label>
          <div class="cw-aktionen">
            <button type="button" class="cw-knopf" :disabled="!kannUebernehmen" data-test="ci-import-uebernehmen" @click="uebernehmen">
              {{ t('uebernehmen', { n: auswahl.anzahl }) }}
            </button>
            <button type="button" class="cw-knopf-leise" @click="schliessen">{{ t('abbrechen') }}</button>
          </div>
        </div>
      </div>

      <div class="cw-vorschau">
        <div class="cw-vorschau-steuer">
          <div class="cw-segment" role="group" :aria-label="t('vorschau')">
            <button
              v-for="r in ['seite', 'katalog', 'webseite']"
              :key="r"
              type="button"
              class="cw-segment-knopf"
              :class="{ aktiv: reiter === r }"
              :aria-pressed="String(reiter === r)"
              :data-test="`ci-import-reiter-${r}`"
              @click="reiter = r"
            >{{ t(`reiter.${r}`) }}</button>
          </div>
          <div v-if="reiter !== 'webseite'" class="cw-segment" role="group" :aria-label="t('vorherNachher')">
            <button
              v-for="n in [false, true]"
              :key="String(n)"
              type="button"
              class="cw-segment-knopf"
              :class="{ aktiv: nachher === n }"
              :aria-pressed="String(nachher === n)"
              :data-test="`ci-import-${n ? 'nachher' : 'vorher'}`"
              @click="nachher = n"
            >{{ t(n ? 'nachher' : 'vorher') }}</button>
          </div>
        </div>
        <ci-brand-vorschau v-if="reiter === 'seite'" :ci="vorschauCi" />
        <katalog-ci-knopf
          v-else-if="reiter === 'katalog'"
          eingebettet
          :ci="vorschauCi"
          :block="nachher ? auswahl.block : undefined"
          :scope="scope"
          :item-id="itemId"
          :is-sys-admin="isSysAdmin"
          :user-id="userId"
        />
        <div v-else class="cw-foto" data-test="ci-import-foto">
          <img v-if="vorschlag.foto" :src="vorschlag.foto" :alt="t('fotoAlt', { domain: kurz ? kurz.domain : '' })">
          <p v-else class="cw-hinweis">{{ t('keinFoto') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WebFontLoader from '@/vendor/webfontloader';
import { ciImportPruefen, typeFontVariants } from '@/editor-v2/utils/ciTokens';
import { waechterZeilen } from '@/editor-v2/utils/ciImport';
import KiKostenZeile from '@/components/helper/KiKostenZeile.vue';
import CiBrandVorschau from './CiBrandVorschau.vue';
import KatalogCiKnopf from './KatalogCiKnopf.vue';
import {
  GRUPPEN, MAX_SEITEN, FEHLT, adressenFehler, pruefeAuswahl, sicherheitStufe, vorausgewaehlt, wertAnPfad, zeilenAus,
} from './ciVonWebseite';
import { ciZuordnen, webseiteLesen } from './ciVonWebseiteAktionen';

// The tile a group belongs to (kit wording, §9.2).
const KACHEL = {
  farben: 'farben', schriften: 'schriften', typografie: 'typo', formen: 'formen', abstaende: 'abstand', knoepfe: 'knopf', logo: 'logo',
};
const BILD = /^(logo|favicon)\.image$/;
const FARB_PFAD = /^(_customColor\.\d|designTokens\.colors\.\w+)$/;
const TYPO_FELD = {
  min: 'groesseHandy', max: 'groesseDesktop', lineHeight: 'zeilenhoehe', ls: 'laufweite',
};
const SCHRIFTFARBE = { '#000000': 'schwarz', '#ffffff': 'weiss' };

// One value: colour chip, colours, font sample, image, shadow sample or text.
const CwWert = {
  functional: true,
  props: { d: { type: Object, required: true } },
  render(h, { props: { d } }) {
    const kids = [];
    if (d.farben) d.farben.forEach((f) => kids.push(h('span', { class: 'cw-chip', style: { background: f } })));
    if (d.bild) kids.push(h('img', { class: 'cw-bild', attrs: { src: d.bild, alt: '' } }));
    if (d.schatten) kids.push(h('span', { class: 'cw-schatten', style: { boxShadow: d.schatten } }));
    if (d.text) kids.push(h('span', { class: 'cw-wert-text', style: d.schrift ? { fontFamily: d.schrift } : {} }, d.text));
    return h('span', { class: ['cw-wert', { 'cw-leer': d.leer }] }, kids);
  },
};

export default {
  name: 'CiVonWebseite',
  components: {
    CiBrandVorschau, KatalogCiKnopf, CwWert, KiKostenZeile,
  },
  props: {
    // The CI the kit holds (also unsaved changes).
    ci: { type: Object, required: true },
    scope: { type: String, required: true },
    itemId: { type: Number, default: null },
    userId: { type: Number, default: null },
    isSysAdmin: { type: Boolean, default: false },
    // Font slots of this user (2, sysadmin 5).
    maxFonts: { type: Number, default: 2 },
  },
  data: () => ({
    schritt: 'eingabe',
    urls: [''],
    erlaubt: false,
    maxSeiten: MAX_SEITEN,
    phase: { lesen: 'wartet', zuordnen: 'wartet' },
    lauf: 0,
    token: null,
    kurz: null,
    fehler: null,
    vorschlag: null,
    an: {},
    reiter: 'seite',
    nachher: true,
    bewusst: false,
    zeigeVerworfene: false,
    geladen: [],
  }),
  computed: {
    adressFehler() {
      return adressenFehler(this.urls);
    },
    kurzText() {
      return this.t('kurz', { domain: this.kurz.domain, n: this.kurz.seiten });
    },
    kurzHinweise() {
      return [...(this.kurz.warnungen || []), ...(this.kurz.robotsHinweis ? [this.t('robots')] : [])];
    },
    zeilen() {
      return zeilenAus(this.vorschlag);
    },
    gruppen() {
      return GRUPPEN.map((key) => {
        const zeilen = this.zeilen.filter((z) => z.gruppe === key && z.status !== 'verworfen');
        return { key, zeilen, alleAn: zeilen.length > 0 && zeilen.every((z) => this.an[z.id]) };
      }).filter((g) => g.zeilen.length);
    },
    verworfene() {
      return this.zeilen.filter((z) => z.status === 'verworfen');
    },
    auswahl() {
      return pruefeAuswahl(this.ci, this.zeilen, this.an, this.maxFonts);
    },
    vorschauCi() {
      return this.nachher ? this.auswahl.gemischt : this.ci;
    },
    schriftRot() {
      return Object.values(this.auswahl.rot).includes('schriftplatzVoll');
    },
    lesbarkeitRot() {
      return Object.values(this.auswahl.rot).includes('lesbarkeit');
    },
    sperreText() {
      if (this.schriftRot) return this.t('sperre.schrift');
      if (this.lesbarkeitRot) {
        const p = this.auswahl.schlecht[0];
        return this.t('sperre.lesbarkeit', { wert: this.zahl(p.verhaeltnis) });
      }
      return '';
    },
    kannUebernehmen() {
      return this.auswahl.anzahl > 0 && !this.schriftRot && (!this.lesbarkeitRot || this.bewusst);
    },
    hinweise() {
      return (this.vorschlag && this.vorschlag.hinweise) || [];
    },
    // What the contrast guard writes on the CI colours of the mixed CI (Lesbarkeit A), as info.
    waechter() {
      return waechterZeilen(this.auswahl.gemischt).map((w) => this.t('waechter', {
        farbe: this.feldName(w.farbe),
        neu: this.schriftfarbe(w.text),
        alt: this.schriftfarbe(w.vorher),
        vorher: this.zahl(w.verhaeltnis[0]),
        nachher: this.zahl(w.verhaeltnis[1]),
      }));
    },
  },
  watch: {
    // The fonts of the mixed CI load in the admin too, else the page preview shows the fallback.
    'auswahl.gemischt.defaultFonts': {
      handler(fonts) {
        const neu = Object.values(fonts || {})
          .filter((f) => f && f.type === 'google' && f.font && !this.geladen.includes(f.font)).map((f) => f.font);
        if (!neu.length) return;
        this.geladen.push(...neu);
        const { designTokens } = this.auswahl.gemischt;
        const varianten = typeFontVariants({ ...(designTokens && designTokens.typography), enabled: true });
        WebFontLoader.load({ google: { families: neu.map((f) => `${f}:${varianten}`) } });
      },
    },
    auswahl() {
      if (!this.lesbarkeitRot) this.bewusst = false;
    },
  },
  methods: {
    t(key, params) {
      return this.$t(`settings.ciBrandKit.import.${key}`, params);
    },
    sicherheitStufe,
    zahl(v) {
      return String(v).replace('.', this.$i18n.locale.startsWith('en') ? '.' : ',');
    },
    schriftfarbe(hex) {
      const k = SCHRIFTFARBE[String(hex).toLowerCase()];
      return k ? this.t(`schriftfarbe.${k}`) : hex;
    },
    gruppenName(key) {
      return key === 'stellschraube' ? this.t('stellschraube') : this.$t(`settings.ciBrandKit.kachel.${KACHEL[key]}`);
    },
    rollenName(rolle) {
      return this.$t(`settings.project.corporateIdentity.typo.rolle.${rolle}`);
    },
    feldName(pfad) {
      const typo = pfad.match(/^designTokens\.typography\.(overrides|roleStyles)\.([a-z0-9]+)\.(\w+)$/);
      if (typo) return this.t(`feld.${TYPO_FELD[typo[3]] || typo[3]}`, { rolle: this.rollenName(typo[2]) });
      const weitere = pfad.match(/^_additionalColors\.(\d+)$/);
      if (weitere) return this.t('feld.weitereFarbe', { n: Number(weitere[1]) + 1 });
      const ecken = pfad.match(/^designTokens\.radiusOverrides\.(\w+)$/);
      if (ecken) return this.t(`feld.ecken.${ecken[1]}`);
      const fest = {
        '_customColor.0': 'grundfarbe',
        '_customColor.1': 'zweiteFarbe',
        _additionalColors: 'weitereFarben',
        'logo.image': 'logo',
        'favicon.image': 'favicon',
        '--wmk-picture-radius': 'bildEcken',
      }[pfad];
      const key = `settings.ciBrandKit.import.feld.${fest || pfad.split('.').pop()}`;
      // A path the import does not know (discarded rows) shows as it came.
      return this.$te(key) ? this.$t(key) : pfad;
    },
    // The value the CI holds now (the kit, also unsaved); a knob is new each time.
    wertJetzt(z) {
      if (z.gruppe === 'stellschraube') return FEHLT;
      const v = wertAnPfad(this.ci, z.pfad);
      return v && v.url !== undefined && BILD.test(z.pfad) ? v.url : v;
    },
    anzeige(pfad, wert) {
      if (wert === FEHLT || wert === null || wert === undefined || wert === '') return { text: this.t('theme'), leer: true };
      if (FARB_PFAD.test(pfad)) return { farben: [wert], text: String(wert) };
      if (pfad === '_additionalColors') return { farben: [].concat(wert) };
      if (BILD.test(pfad)) return { bild: String(wert) };
      if (/(Font|\.schrift)$/.test(pfad)) return { text: String(wert), schrift: `'${wert}', sans-serif` };
      if (wert === true || wert === false) return { text: this.t(wert ? 'ja' : 'nein') };
      if (pfad === 'designTokens.cardShadow') {
        return { schatten: `0 ${wert.y}px ${wert.blur}px color-mix(in srgb, ${wert.color} ${wert.opacity}%, transparent)`, text: `${wert.y} · ${wert.blur} · ${wert.opacity} %` };
      }
      if (pfad === 'designTokens.buttonPadding') return { text: `${wert.y} × ${wert.x} px` };
      if (pfad === '--wmk-picture-radius') return { text: `${parseFloat(wert)} px` };
      if (/\.(min|max)$/.test(pfad)) return { text: `${Math.round(wert * 16)} px` };
      if (pfad.endsWith('.ls')) return { text: `${this.zahl(wert)} em` };
      if (/(contentWidth|radiusCustom)$/.test(pfad) || /radiusOverrides/.test(pfad)) {
        return { text: Number.isFinite(wert) ? `${wert} px` : this.wortWert(wert) };
      }
      return { text: this.wortWert(wert) };
    },
    // Words for list values (stages, styles); unknown ones as written.
    wortWert(wert) {
      const key = `settings.ciBrandKit.import.wert.${wert}`;
      return this.$te(key) ? this.$t(key) : this.zahl(wert);
    },
    markText(z) {
      const rot = this.auswahl.rot[z.id];
      if (rot) return this.t(`meldung.${rot}`);
      if (z.status === 'lesbarkeit') return this.t('meldung.lesbarkeitZweiteWahl');
      if (z.status === 'angepasst') return this.t('meldung.grenze');
      return '';
    },
    markRot(z) {
      return !!this.auswahl.rot[z.id] || z.status === 'lesbarkeit';
    },
    gruppeSetzen(g, wert) {
      g.zeilen.forEach((z) => this.$set(this.an, z.id, wert));
    },
    sprache() {
      return this.$i18n.locale.startsWith('en') ? 'en' : 'de';
    },
    // Step 1, then at once step 2. A newer run (or Abbrechen) makes the answer of an older one void.
    async lesen() {
      if (this.adressFehler || !this.erlaubt) return;
      this.lauf += 1;
      const { lauf } = this;
      Object.assign(this, {
        schritt: 'warten', fehler: null, token: null, kurz: null, phase: { lesen: 'laeuft', zuordnen: 'wartet' },
      });
      try {
        const r = await webseiteLesen(this.$apollo, {
          ziel: this.scope, id: this.itemId, urls: this.urls.map((u) => u.trim()).filter(Boolean), sprache: this.sprache(),
        });
        if (lauf !== this.lauf) return;
        this.token = r.token;
        this.kurz = r.kurz;
        this.phase.lesen = 'fertig';
      } catch (err) {
        if (lauf !== this.lauf) return;
        this.phase.lesen = 'fehler';
        this.fehler = err.message;
        return;
      }
      this.zuordnen();
    },
    async zuordnen() {
      const { lauf } = this;
      this.fehler = null;
      this.phase.zuordnen = 'laeuft';
      try {
        const vorschlag = await ciZuordnen(this.$apollo, this.token);
        if (lauf !== this.lauf) return;
        this.phase.zuordnen = 'fertig';
        this.vorschlag = vorschlag;
        this.an = Object.fromEntries(zeilenAus(vorschlag).map((z) => [z.id, z.status !== 'verworfen' && vorausgewaehlt(z)]));
        this.schritt = 'pruefen';
      } catch (err) {
        if (lauf !== this.lauf) return;
        this.phase.zuordnen = 'fehler';
        this.fehler = err.message;
      }
    },
    // Abbrechen: the server finishes its step, the answer is dropped; between the steps the AI call never starts.
    zurEingabe() {
      this.lauf += 1;
      Object.assign(this, {
        schritt: 'eingabe', fehler: null, token: null, kurz: null,
      });
    },
    schliessen() {
      this.lauf += 1;
      // The photo (up to 400 KB) does not stay in memory (M5).
      this.vorschlag = null;
      this.$emit('schliessen');
    },
    // C7: every ticked field runs through ciImportPruefen once more; the parent writes with today's methods.
    uebernehmen() {
      if (!this.kannUebernehmen) return;
      const gewaehlt = this.zeilen.filter((z) => this.an[z.id] && z.status !== 'verworfen' && z.gruppe !== 'stellschraube');
      const felder = gewaehlt.flatMap((z) => {
        const r = ciImportPruefen(z.pfad, z.wert);
        return r.status === 'verworfen' ? [] : [{ pfad: z.pfad, wert: r.wert }];
      });
      this.$emit('uebernehmen', {
        felder,
        block: this.auswahl.block,
        urls: this.urls.map((u) => u.trim()).filter(Boolean),
        domain: this.kurz ? this.kurz.domain : '',
        anzahl: this.auswahl.anzahl,
      });
      this.vorschlag = null;
    },
  },
};
</script>

<style scoped>
/* Brand kit surfaces (CiBrandKit.vue): calm fills, one accent, scale 4/8/12/16/24/32. */
.cw { display: flex; flex-direction: column; gap: 16px; }
.cw-kosten { margin-bottom: 8px; }
/* Readable on the dark brand-kit surface (same grey as .cw-gruppe-titel). */
.cw-liste .cw-kosten { color: #a9acb1; }
.cw-kopf { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
.cw-titel { margin: 0; font-size: 20px; font-weight: 700; color: #fff; }
.cw-unter { margin: 4px 0 0; font-size: 14px; line-height: 1.5; color: #c9cbce; max-width: 60ch; }
.cw-karte {
  background: #44474d; border-radius: 12px; padding: 24px 28px; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  display: flex; flex-direction: column; gap: 12px; max-width: 640px;
}
.cw-feld-label { margin: 0; font-size: 14px; font-weight: 600; color: #fff; text-transform: none; letter-spacing: normal; }
.cw-url-zeile { display: flex; align-items: center; gap: 8px; }
.cw-input {
  flex: 1; min-width: 0; height: 40px; padding: 0 12px; border: 1px solid #5b6064; border-radius: 8px;
  background: #36393d; color: #fff; font-size: 15px;
}
.cw-input::placeholder { color: #8a8d92; }
.cw-input:focus { outline: 2px solid #7fd6a4; outline-offset: 1px; border-color: transparent; }
.cw-hinweis { margin: 4px 0 0; font-size: 13px; line-height: 1.5; color: #a9acb1; }
.cw-haken {
  display: flex; gap: 12px; align-items: flex-start; margin: 0; cursor: pointer;
  font-size: 14px; line-height: 1.45; color: #e9e9e7; text-transform: none; letter-spacing: normal; font-weight: 500;
}
.cw-haken input { width: 18px; height: 18px; margin-top: 1px; flex: none; accent-color: #7fd6a4; }
.cw-aktionen { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-top: 8px; }
.cw-knopf {
  all: unset; box-sizing: border-box; cursor: pointer; display: inline-flex; align-items: center; height: 40px;
  padding: 0 20px; border-radius: 8px; background: #7fd6a4; color: #1d2b22; font-size: 14px; font-weight: 700;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
}
.cw-knopf:hover { background: #a6e4bf; }
.cw-knopf:disabled { cursor: not-allowed; background: #5b6064; color: #a9acb1; box-shadow: none; }
.cw-knopf-leise { all: unset; cursor: pointer; height: 40px; padding: 0 12px; color: #d9dadc; font-size: 14px; font-weight: 600; }
.cw-knopf-leise:hover { color: #fff; }
.cw-link { all: unset; cursor: pointer; color: #7fd6a4; font-size: 14px; font-weight: 600; }
.cw-link:hover { color: #a6e4bf; }
.cw-knopf:focus-visible, .cw-knopf-leise:focus-visible, .cw-link:focus-visible, .cw-aufklapp:focus-visible,
.cw-segment-knopf:focus-visible { outline: 2px solid #7fd6a4; outline-offset: 2px; border-radius: 6px; }
.cw-weg { font-size: 20px; padding: 0 8px; color: #a9acb1; }
.cw-mehr { align-self: flex-start; }
.cw-fehler-text { margin: 0; font-size: 14px; line-height: 1.45; color: #ffb4b4; }

/* The admin styles every label in a tab pane (uppercase, letter spacing) with an id selector. */
.cw-zeile, .cw-haken { text-transform: none !important; letter-spacing: normal !important; }

/* Step 2 */
.cw-schritte { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 16px; }
.cw-schritt { display: flex; gap: 12px; align-items: flex-start; }
.cw-schritt-zeichen {
  width: 20px; height: 20px; flex: none; border-radius: 50%; border: 2px solid #6a6d73; box-sizing: border-box; margin-top: 1px;
}
.cw-laeuft .cw-schritt-zeichen { border-color: #7fd6a4; border-right-color: transparent; animation: cw-dreh 0.9s linear infinite; }
.cw-fertig .cw-schritt-zeichen { background: #7fd6a4; border-color: #7fd6a4; }
.cw-fehler .cw-schritt-zeichen { background: #e46a6a; border-color: #e46a6a; }
@keyframes cw-dreh { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .cw-laeuft .cw-schritt-zeichen { animation: none; } }
.cw-schritt-text { display: flex; flex-direction: column; gap: 4px; }
.cw-schritt-titel { font-size: 15px; font-weight: 600; color: #fff; }
.cw-wartet .cw-schritt-titel { color: #a9acb1; font-weight: 500; }
.cw-schritt-info { font-size: 13px; color: #c9cbce; }

/* Step 3: list left, preview right */
.cw-pruefen { display: flex; flex-wrap: wrap; gap: 32px; align-items: flex-start; }
.cw-pruefen > * { min-width: 0; }
.cw-liste { flex: 1 1 520px; max-width: 640px; display: flex; flex-direction: column; gap: 12px; }
.cw-vorschau { flex: 1 1 420px; position: sticky; top: 80px; display: flex; flex-direction: column; gap: 12px; }
.cw-gruppe { background: #44474d; border-radius: 12px; padding: 16px 8px 8px; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2); }
.cw-gruppe-kopf { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; padding: 0 12px 8px; }
.cw-gruppe-titel { margin: 0; font-size: 12px; letter-spacing: 1px; font-weight: 700; color: #a9acb1; text-transform: uppercase; }
.cw-zahl { margin-left: 6px; color: #7d8086; }
.cw-zeile {
  display: grid; grid-template-columns: 20px minmax(0, 1fr) auto; gap: 12px; align-items: start; margin: 0;
  padding: 12px; border-radius: 8px; cursor: pointer; text-transform: none; letter-spacing: normal; font-weight: 400;
}
.cw-zeile:hover { background: #4a4d54; }
.cw-rot { box-shadow: inset 3px 0 0 #e46a6a; background: rgba(228, 106, 106, 0.08); }
.cw-check { width: 18px; height: 18px; margin: 2px 0 0; accent-color: #7fd6a4; }
.cw-zeile-text { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.cw-name { font-size: 15px; font-weight: 600; color: #fff; }
.cw-grund { font-size: 13px; line-height: 1.4; color: #c9cbce; }
.cw-punkt { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 4px; vertical-align: 1px; }
.cw-hoch { background: #7fd6a4; }
.cw-mittel { background: #f5c86b; }
.cw-niedrig { background: #7d8086; }
.cw-mark { align-self: flex-start; font-size: 12px; font-weight: 600; padding: 2px 8px; border-radius: 999px; color: #f5c86b; background: rgba(245, 200, 107, 0.12); }
.cw-mark-rot { color: #ffb4b4; background: rgba(228, 106, 106, 0.16); }
.cw-werte { display: flex; align-items: center; gap: 8px; justify-content: flex-end; max-width: 240px; flex-wrap: wrap; }
.cw-pfeil { color: #7d8086; }
.cw-werte ::v-deep .cw-wert { display: inline-flex; align-items: center; gap: 6px; min-width: 0; font-size: 13px; color: #e9e9e7; }
.cw-werte ::v-deep .cw-leer { color: #8a8d92; font-style: italic; }
.cw-werte ::v-deep .cw-chip { width: 20px; height: 20px; border-radius: 5px; box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.2); flex: none; }
.cw-werte ::v-deep .cw-wert-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 120px; }
.cw-werte ::v-deep .cw-bild { height: 24px; max-width: 80px; object-fit: contain; background: #f4f4f2; border-radius: 4px; padding: 2px 4px; }
.cw-werte ::v-deep .cw-schatten { width: 24px; height: 18px; border-radius: 4px; background: #fff; flex: none; }
.cw-notizen { padding: 16px 20px; display: flex; flex-direction: column; gap: 8px; }
.cw-notiz { margin: 0; font-size: 13px; line-height: 1.5; color: #c9cbce; }
.cw-notiz-waechter { color: #e9e9e7; }
.cw-verworfen { padding: 8px 12px; }
.cw-aufklapp {
  all: unset; cursor: pointer; display: flex; justify-content: space-between; align-items: center; width: 100%;
  box-sizing: border-box; padding: 8px; color: #d9dadc; font-size: 14px; font-weight: 600;
}
.cw-chevron { width: 8px; height: 8px; border-right: 2px solid currentColor; border-bottom: 2px solid currentColor; transform: rotate(45deg); transition: transform 0.15s; }
.cw-chevron.offen { transform: rotate(-135deg); }
.cw-verworfen .cw-notiz { padding: 4px 8px; }
.cw-fuss {
  position: sticky; bottom: 0; z-index: 1; display: flex; flex-direction: column; gap: 8px;
  padding: 16px 20px; background: #3d4045; border-radius: 12px; box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.25);
}
.cw-fuss .cw-aktionen { margin-top: 0; }
.cw-vorschau-steuer { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px; }
.cw-segment { display: inline-flex; gap: 2px; padding: 3px; background: #36393d; border-radius: 10px; }
.cw-segment-knopf {
  all: unset; cursor: pointer; height: 32px; padding: 0 12px; border-radius: 8px; display: inline-flex; align-items: center;
  color: #d9dadc; font-size: 14px; font-weight: 500;
}
.cw-segment-knopf:hover { background: rgba(255, 255, 255, 0.06); color: #fff; }
.cw-segment-knopf.aktiv { background: #5b6064; color: #fff; font-weight: 700; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3); }
.cw-foto { background: #36393d; border-radius: 12px; padding: 12px; }
.cw-foto img { display: block; width: 100%; height: auto; border-radius: 8px; }
@media (max-width: 700px) {
  .cw-karte { padding: 20px; }
  .cw-zeile { grid-template-columns: 20px minmax(0, 1fr); }
  .cw-werte { grid-column: 2; justify-content: flex-start; max-width: none; }
  .cw-vorschau { position: static; }
}
</style>
